import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/shared/lib/cn";

const inputVariants = cva(
  "flex w-full rounded-[6px] border bg-white px-[12px] py-[8px] text-base text-left ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-gray-400 transition-colors focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100",
  {
    variants: {
      size: {
        default: "h-auto",
        sm: "h-8 text-sm",
        lg: "h-auto text-base",
      },
      variant: {
        default: "border-gray-400 hover:border-gray-500",
        error: "border-destructive focus-visible:ring-destructive",
        success: "border-green-500 focus-visible:ring-green-500",
      },
    },
    defaultVariants: {
      size: "default",
      variant: "default",
    },
  }
);

export interface InputProps
  extends Omit<React.ComponentProps<"input">, "size">,
    VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size, variant, onWheel, onChange, min, ...props }, ref) => {
    const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
      if (type === "number") {
        e.currentTarget.blur();
      }
      onWheel?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (type === "number") {
        const value = e.target.value;
        if (value === "" || value === "-") {
          onChange?.(e);
          return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          const minValue = min !== undefined ? parseFloat(String(min)) : 0;
          if (numValue < minValue) {
            e.target.value = String(minValue);
            const syntheticEvent = {
              ...e,
              target: { ...e.target, value: String(minValue) },
              currentTarget: { ...e.currentTarget, value: String(minValue) },
            } as React.ChangeEvent<HTMLInputElement>;
            onChange?.(syntheticEvent);
            return;
          }
        }
      }
      onChange?.(e);
    };

    return (
      <input
        type={type}
        className={cn(
          inputVariants({ size, variant, className }),
          type === "number" && "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        )}
        ref={ref}
        onWheel={handleWheel}
        onChange={handleChange}
        min={type === "number" && min === undefined ? 0 : min}
        {...props}
      />
    );
  }
);
Input.displayName = "Input";

export { Input };
